<script>
	export let goToFaceAnalysisResult;
    import { onMount, onDestroy } from 'svelte';
    import { writable } from 'svelte/store';
    import { loader } from '../components/loader';
    import { isNative, nativePhoto } from '../native.js';

	let loading = writable(false);

	let videoElement;
	let stream = null;

	// Scratch canvas for the brightness meter ONLY. The capture path builds its
	// own canvas per shot (see captureToCanvas) instead of reusing a long-lived
	// element, so a context created before the element was sized can never be
	// reused, and the capture canvas never carries willReadFrequently.
	let meterCanvas = null;

	// 'starting' -> 'live' -> 'review' -> 'analyzing'
	let phase = 'starting';
	let cameraError = '';

	// Where the photo under review came from. The camera path mirrors its
	// capture to match the preview the user framed; an uploaded file must not
	// be mirrored, and "Retake" means something different for each.
	let source = 'camera';   // 'camera' | 'upload' | 'native-camera'
	let fileInput;
	let uploadError = '';

	// Inside the app rather than a browser tab. The webview's own getUserMedia
	// still drives the live view, because the oval guide, the brightness
	// coaching and the blank-frame check are worth more than a system camera
	// sheet. What the phone adds is a real photo picker, and a way out if the
	// webview camera will not start.
	const native = isNative();

	// A live camera was the only way into this product. Deny the permission, be
	// on a desktop without a camera, or sit at a machine where IT blocks it,
	// and the journey ended at "allow the camera and reload this page" -- with
	// every other screen behind that one prompt.
	const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;
	// The camera path captures at twice the video dimensions, roughly 2560 wide.
	// Match that rather than sending a 48-megapixel phone original.
	const MAX_UPLOAD_EDGE = 2560;
	let warningMessage = '';
	let capturedPreview = '';
	let capturedBlob = null;
	let analysisFailed = false;
	let captureError = '';

	// Below this mean luminance (0-255) a frame carries no picture. A dim room
	// still reads 25-40; a camera that is connected but sending nothing reads 0.
	const BLANK_FRAME_THRESHOLD = 6;

	let brightnessTimer = null;
	let stageTimer = null;

	// The analysis takes a while. Staring at a frozen picture of your own face
	// with a single pulsing line for two minutes is an eternity, so narrate it.
	const ANALYSIS_STAGES = [
		'Uploading your photo…',
		'Detecting your face…',
		'Reading skin texture…',
		'Scoring each concern…',
		'Matching products to your profile…',
		'Almost there, putting your profile together…'
	];
	let stageIndex = 0;
	$: analysisStage = ANALYSIS_STAGES[Math.min(stageIndex, ANALYSIS_STAGES.length - 1)];

	onMount(async () => {
		await setupWebcam();
	});

	onDestroy(() => {
		stopBrightnessWatch();
		stopStageTicker();
		releaseCamera();
	});

	async function setupWebcam() {
		phase = 'starting';
		cameraError = '';

		// A face is taller than it is wide. The old code asked for 4:3 landscape
		// on mobile, which framed the user's head as a small slice of a wide
		// box; ask for portrait on narrow screens instead.
		const isNarrow = typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;
		const aspectRatio = isNarrow ? { ideal: 0.75 } : { ideal: 1.3333 };

		try {
			stream = await navigator.mediaDevices.getUserMedia({
				video: { aspectRatio, facingMode: 'user' }
			});
		} catch (error) {
			console.error('Error initializing camera:', error);
			const blocked = error && error.name === 'NotAllowedError';
			if (native) {
				cameraError = blocked
					? 'SkinGenie does not have camera access yet. You can take a photo with the camera below, or allow it in Settings.'
					: 'We couldn’t reach the camera. You can take a photo with the camera below instead.';
			} else {
				cameraError = blocked
					? 'SkinGenie needs camera access to analyse your skin. Allow the camera in your browser settings, then reload this page.'
					: 'We couldn’t reach your camera. Check that no other app is using it, then reload this page.';
			}
			return;
		}

		if ('srcObject' in videoElement) {
			videoElement.srcObject = stream;
		} else {
			videoElement.src = window.URL.createObjectURL(stream);
		}

		// Assigning the handler AFTER srcObject is a race: if metadata has already
		// loaded, onloadedmetadata never fires again and the promise never
		// settles, leaving the user on "Starting your camera..." forever. Check
		// the current state first, listen second, and time out rather than hang.
		await new Promise((resolve) => {
			if (videoElement.readyState >= 1 && videoElement.videoWidth) return resolve();

			let settled = false;
			const done = () => {
				if (settled) return;
				settled = true;
				videoElement.removeEventListener('loadedmetadata', done);
				resolve();
			};
			videoElement.addEventListener('loadedmetadata', done);
			setTimeout(done, 5000);
		});

		phase = 'live';
		startBrightnessWatch();
	}

	function releaseCamera() {
		if (stream) {
			stream.getTracks().forEach((track) => track.stop());
			stream = null;
		}
		if (videoElement) {
			videoElement.srcObject = null;
		}
	}

	function startBrightnessWatch() {
		stopBrightnessWatch();
		brightnessTimer = setInterval(checkBrightness, 1000);
	}

	function stopBrightnessWatch() {
		if (brightnessTimer) {
			clearInterval(brightnessTimer);
			brightnessTimer = null;
		}
	}

	function startStageTicker() {
		stageIndex = 0;
		stopStageTicker();
		stageTimer = setInterval(() => {
			if (stageIndex < ANALYSIS_STAGES.length - 1) stageIndex += 1;
		}, 18000);
	}

	function stopStageTicker() {
		if (stageTimer) {
			clearInterval(stageTimer);
			stageTimer = null;
		}
	}

	// One canvas per capture, created fresh, with NO willReadFrequently.
	//
	// The old code drew the video into a single long-lived canvas whose context
	// was created with { willReadFrequently: true }. That flag forces a
	// CPU-backed surface, and on some Chrome/macOS builds a hardware-decoded
	// camera frame will not composite into one -- you get a fully black image
	// from a camera that reports live, unmuted, readyState 4. Headless Chromium
	// decodes in software, which is why it never reproduced in testing.
	//
	// Returns null if the video has no dimensions yet.
	function captureToCanvas() {
		const w = videoElement && videoElement.videoWidth;
		const h = videoElement && videoElement.videoHeight;
		if (!w || !h) return null;

		const canvas = document.createElement('canvas');
		canvas.width = w * 2;    // capture at 2x for the analyzer
		canvas.height = h * 2;

		const context = canvas.getContext('2d');
		// Mirror, so the saved photo matches the mirrored preview the user framed.
		// translate + scale rather than a negative destination x: same result,
		// no negative coordinates for an implementation to disagree about.
		context.translate(canvas.width, 0);
		context.scale(-1, 1);
		context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
		context.setTransform(1, 0, 0, 1, 0, 0);
		return canvas;
	}

	// Mean luminance of any canvas, measured on a small scratch surface. Reading
	// pixels back from a 2x capture would be slow, and the source here is always
	// a canvas rather than a video, so the CPU-backed read is safe.
	function meanLuminance(sourceCanvas) {
		if (!meterCanvas) {
			meterCanvas = document.createElement('canvas');
			meterCanvas.width = 160;
			meterCanvas.height = 120;
		}

		const context = meterCanvas.getContext('2d', { willReadFrequently: true });
		context.clearRect(0, 0, meterCanvas.width, meterCanvas.height);
		context.drawImage(sourceCanvas, 0, 0, meterCanvas.width, meterCanvas.height);

		const pixels = context.getImageData(0, 0, meterCanvas.width, meterCanvas.height).data;
		let total = 0;
		for (let i = 0; i < pixels.length; i += 4) {
			total += 0.299 * pixels[i] + 0.587 * pixels[i + 1] + 0.114 * pixels[i + 2];
		}
		return total / (pixels.length / 4);
	}

	// readyState >= 2 means dimensions AND pixel data. loadedmetadata only
	// promises dimensions, so capturing on it can draw a frame that was never
	// painted. requestVideoFrameCallback waits for an actual presented frame.
	function waitForFrame() {
		return new Promise((resolve) => {
			let settled = false;
			const done = () => {
				if (settled) return;
				settled = true;
				resolve();
			};

			setTimeout(done, 1200);

			if (videoElement.readyState >= 2) {
				if (typeof videoElement.requestVideoFrameCallback === 'function') {
					videoElement.requestVideoFrameCallback(done);
				} else {
					requestAnimationFrame(done);
				}
				return;
			}

			videoElement.addEventListener('loadeddata', done, { once: true });
		});
	}

	// Capture, then let the user look at it before anything is uploaded.
	async function capturePhoto() {
		if (phase !== 'live') return;

		captureError = '';
		await waitForFrame();

		let canvas = captureToCanvas();
		let luminance = canvas ? meanLuminance(canvas) : 0;

		// One retry. A single blank frame can just be bad timing; a second blank
		// one means the pipeline genuinely is not delivering pixels.
		if (!canvas || luminance < BLANK_FRAME_THRESHOLD) {
			await waitForFrame();
			canvas = captureToCanvas();
			luminance = canvas ? meanLuminance(canvas) : 0;
		}

		// Never hand the user a black photo and a "Use This Photo" button. The
		// analyzer would either fail or return nonsense, and they would have no
		// idea why. Stay on the live view and say what happened.
		if (!canvas || luminance < BLANK_FRAME_THRESHOLD) {
			captureError =
				'That photo came out black. Your camera is connected but is not sending a picture. ' +
				'Close any other app that might be using it, such as a video call or Photo Booth, then try again.';
			return;
		}

		capturedPreview = canvas.toDataURL('image/png');

		videoElement.pause();
		stopBrightnessWatch();
		warningMessage = '';
		phase = 'review';

		// toBlob is async. Await it rather than firing and forgetting, so a fast
		// tap on "Use This Photo" can't find capturedBlob still null and
		// silently do nothing.
		capturedBlob = await new Promise((resolve) => {
			canvas.toBlob(resolve, 'image/png');
		});
	}

	function chooseFile() {
		uploadError = '';
		captureError = '';
		if (native) {
			chooseNativePhoto();
			return;
		}
		if (fileInput) fileInput.click();
	}

	// Decoded with the file's own EXIF orientation applied. A photo taken on a
	// phone held sideways carries a rotation flag that drawImage ignores, so
	// without this the analyzer gets a face lying on its side.
	async function decode(file) {
		if (typeof createImageBitmap === 'function') {
			try {
				return await createImageBitmap(file, { imageOrientation: 'from-image' });
			} catch (error) {
				// Older Safari rejects the options bag rather than ignoring it.
				try {
					return await createImageBitmap(file);
				} catch (ignored) { /* fall through to the <img> path */ }
			}
		}

		const url = URL.createObjectURL(file);
		try {
			return await new Promise((resolve, reject) => {
				const image = new Image();
				image.onload = () => resolve(image);
				image.onerror = () => reject(new Error('decode failed'));
				image.src = url;
			});
		} finally {
			URL.revokeObjectURL(url);
		}
	}

	async function onFileChosen(event) {
		const input = event.target;
		const file = input.files && input.files[0];
		// Cleared so that picking the same file again still fires a change event.
		input.value = '';
		if (!file) return;
		await acceptPhoto(file, 'upload');
	}

	// The phone's own picker, which is what a file input should have been.
	async function chooseNativePhoto() {
		uploadError = '';
		captureError = '';
		try {
			const blob = await nativePhoto('photos');
			if (blob) await acceptPhoto(blob, 'upload');
		} catch (error) {
			console.error('Native photo picker failed:', error);
			uploadError = 'We could not open your photos. Try taking a new photo instead.';
		}
	}

	// The phone's own camera. Only reachable when the webview camera failed,
	// which on a phone usually means the permission was declined once.
	async function takeNativePhoto() {
		uploadError = '';
		captureError = '';
		try {
			const blob = await nativePhoto('camera');
			if (blob) await acceptPhoto(blob, 'native-camera');
		} catch (error) {
			console.error('Native camera failed:', error);
			uploadError =
				'We could not open the camera. Allow camera access for SkinGenie in ' +
				'Settings, or choose a photo you already have.';
		}
	}

	async function acceptPhoto(file, from) {
		uploadError = '';
		captureError = '';

		if (!file.type || !file.type.startsWith('image/')) {
			uploadError = 'That file is not an image. Choose a JPG, PNG or HEIC photo of your face.';
			return;
		}
		if (file.size > MAX_UPLOAD_BYTES) {
			uploadError = 'That photo is larger than 20MB. Choose a smaller one, or take a new photo.';
			return;
		}

		let bitmap;
		try {
			bitmap = await decode(file);
		} catch (error) {
			console.error('Could not decode the chosen file:', error);
			uploadError = 'We could not open that photo. Try a different one, or a JPG or PNG.';
			return;
		}

		const sourceWidth = bitmap.width || bitmap.naturalWidth;
		const sourceHeight = bitmap.height || bitmap.naturalHeight;
		if (!sourceWidth || !sourceHeight) {
			uploadError = 'That photo appears to be empty. Try a different one.';
			return;
		}

		const scale = Math.min(1, MAX_UPLOAD_EDGE / Math.max(sourceWidth, sourceHeight));
		const canvas = document.createElement('canvas');
		canvas.width = Math.round(sourceWidth * scale);
		canvas.height = Math.round(sourceHeight * scale);
		// No mirroring here. The camera mirrors so the saved photo matches the
		// mirrored preview; an uploaded photo is already the right way round.
		canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
		if (typeof bitmap.close === 'function') bitmap.close();

		if (meanLuminance(canvas) < BLANK_FRAME_THRESHOLD) {
			uploadError =
				'That photo is almost entirely black, so there is nothing to read. ' +
				'Choose one taken in better light.';
			return;
		}

		capturedPreview = canvas.toDataURL('image/png');
		source = from;
		warningMessage = '';
		stopBrightnessWatch();
		if (videoElement && stream) videoElement.pause();
		phase = 'review';

		capturedBlob = await new Promise((resolve) => {
			canvas.toBlob(resolve, 'image/png');
		});
	}

	function retake() {
		capturedPreview = '';
		capturedBlob = null;
		captureError = '';
		uploadError = '';

		// Going back must not assume there is a live view to return to. Without
		// a stream the viewfinder never arrives, and the user would be left
		// looking at a dead box with no way forward.
		if (source === 'native-camera') {
			takeNativePhoto();
			return;
		}
		if (source === 'upload' && !stream) {
			source = 'camera';
			chooseFile();
			return;
		}

		source = 'camera';
		phase = 'live';
		if (videoElement) videoElement.play();
		startBrightnessWatch();
	}

	async function submitPhoto() {
		if (phase !== 'review' || !capturedBlob) return;

		phase = 'analyzing';
		startStageTicker();
		loading.set(true);

		const formData = new FormData();
		formData.append('selfie', capturedBlob, 'selfie.png');

		try {
			const response = await fetch('/api/face_analyzer', { method: 'POST', body: formData });

			if (response.ok) {
				const data = await response.json();

				const concerns = Array.isArray(data.areas_of_concern) ? data.areas_of_concern : [];
				const strengths = Array.isArray(data.areas_of_strength) ? data.areas_of_strength : [];

				// If the analyzer read nothing at all, do NOT continue to a page
				// headed "Your skin profile is ready". Say what happened and let
				// the user try again.
				if (concerns.length === 0 && strengths.length === 0) {
					analysisFailed = true;
					return;
				}

				// Persist the findings independently of the annotated image.
				// These used to be written only `if (data.annotated_image)`, so
				// whenever the image came back empty -- which the Flask layer does
				// on its own error path -- every finding was silently thrown away
				// and the user still landed on "Your skin profile is ready" with
				// nothing detected.
				localStorage.setItem('AoI', JSON.stringify(concerns));
				localStorage.setItem('AoS', JSON.stringify(strengths));

				if (data.annotated_image) {
					localStorage.setItem('base64Selfie', data.annotated_image);
				} else {
					localStorage.removeItem('base64Selfie');
				}

				// The raw selfie is deliberately NOT written to localStorage. It used
				// to be stored as `originalBase64Selfie` and left there indefinitely,
				// so a full-resolution photo of the user's face persisted on any
				// shared or public computer long after they'd finished.
				localStorage.removeItem('originalBase64Selfie');

				warningMessage = '';
				releaseCamera();
				goToFaceAnalysisResult();
				return;
			}

			console.error('Fetch failed:', response.status, response.statusText);
			warningMessage = 'Analysis is unavailable right now. Please try again in a moment.';
		} catch (error) {
			console.error('Fetch error:', error);
			warningMessage = 'Analysis is unavailable right now. Please try again in a moment.';
		} finally {
			stopStageTicker();
			loading.set(false);
			if (phase === 'analyzing' && !analysisFailed) {
				phase = 'review';   // stay on the captured photo so they can retry
			}
		}
	}

	function tryAgainAfterFailure() {
		analysisFailed = false;
		retake();
	}

	let averageBrightness = 100;
	const BRIGHT_THRESHOLD = 200;
	const DARK_THRESHOLD = 50;

	function checkBrightness() {
		if (phase !== 'live' || !videoElement) return;

		const frame = captureToCanvas();
		if (!frame) return;

		averageBrightness = meanLuminance(frame);

		if (averageBrightness > BRIGHT_THRESHOLD) {
			warningMessage = 'It\u2019s a little bright \u2014 try facing away from the window or a lamp.';
		} else if (averageBrightness < DARK_THRESHOLD) {
			warningMessage = 'It\u2019s a bit dark \u2014 move somewhere brighter so we can see your skin clearly.';
		} else {
			warningMessage = '';
		}
	}
</script>

<style>
	.capture-page {
		max-width: 720px;
		margin: 0 auto;
		padding: var(--space-28) var(--space-6) var(--space-16);
		font-family: var(--font-body);
	}

	.page-head {
		display: flex;
		align-items: flex-start;
		justify-content: space-between;
		gap: var(--space-6);
		margin-bottom: var(--space-3);
	}

	h1 {
		font-family: var(--font-display);
		font-size: var(--text-2xl);
		font-weight: var(--weight-regular);
		color: var(--cream);
		margin: 0;
		line-height: 1.2;
	}

	.back-link {
		flex: 0 0 auto;
		background: transparent;
		color: var(--text-muted);
		border: 1px solid var(--border);
		padding: var(--space-2) var(--space-5);
		font-size: var(--text-2xs);
		letter-spacing: 2px;
		text-transform: uppercase;
		text-decoration: none;
		white-space: nowrap;
		line-height: 1;
		transition: all var(--dur);
		font-family: var(--font-body);
		cursor: pointer;
	}

	.back-link:hover {
		border-color: var(--rose);
		color: var(--rose);
	}

	.tips {
		margin: 0 0 var(--space-5);
		padding: 0;
		list-style: none;
		display: flex;
		flex-wrap: wrap;
		gap: var(--space-2) var(--space-5);
	}

	.tips li {
		font-size: var(--text-xs);
		line-height: 1.6;
		color: var(--text-muted);
		position: relative;
		padding-left: var(--space-4);
	}

	.tips li::before {
		content: '';
		position: absolute;
		left: 0;
		top: 8px;
		width: 5px;
		height: 5px;
		border-radius: var(--radius-circle);
		background: var(--sage);
	}

	/* --- viewfinder ------------------------------------------------------ */

	.viewfinder {
		position: relative;
		width: 100%;
		aspect-ratio: 4 / 3;
		background: var(--bg);
		border: 1px solid var(--border);
		border-radius: 8px;
		overflow: hidden;
	}

	/* Both fill the viewfinder and OVERLAP. They used to be in normal flow, so
	   in the review phase the hidden <video> still occupied its full height
	   (visibility:hidden reserves layout space) and pushed the captured <img>
	   below the box, where overflow:hidden clipped it away. The photo was
	   captured correctly the whole time -- measured mean luminance 113 against
	   a live video of 111 -- but what rendered was the viewfinder's own black
	   background, which looked exactly like a black photo. */
	.viewfinder video,
	.viewfinder .captured {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
	}

	.viewfinder video {
		transform: scaleX(-1);   /* mirror the preview so it behaves like a mirror */
	}

	/* The guide is drawn against the viewfinder itself, so it actually lines up
	   with the video rather than floating over the whole page. */
	.face-guide {
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		height: 82%;
		aspect-ratio: 3 / 4;
		border: 2px dashed var(--sage-glow);
		border-radius: var(--radius-circle);
		pointer-events: none;
	}

	.guide-hint {
		position: absolute;
		left: 50%;
		bottom: 14px;
		transform: translateX(-50%);
		font-size: var(--text-3xs);
		letter-spacing: 2px;
		text-transform: uppercase;
		color: var(--sage);
		background: var(--scrim);
		padding: var(--space-1) var(--space-3);
		border-radius: 4px;
		pointer-events: none;
		white-space: nowrap;
	}

	.camera-error,
	.starting {
		position: absolute;
		inset: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		text-align: center;
		padding: var(--space-8);
		color: var(--text-muted);
		font-size: var(--text-sm);
		line-height: 1.7;
	}

	.capture-error {
		max-width: 46ch;
		margin: 0 auto var(--space-3);
		padding: var(--space-3);
		border: 1px solid var(--danger);
		border-left-width: 3px;
		background: var(--danger-bg);
		color: var(--text);
		font-size: var(--text-sm);
		line-height: var(--leading-normal);
		text-align: left;
	}

	/* --- controls, below the viewfinder ---------------------------------- */

	.controls {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: var(--space-3);
		margin-top: var(--space-6);
	}

	.control-row {
		display: flex;
		gap: var(--space-3);
		flex-wrap: wrap;
		justify-content: center;
		width: 100%;
	}

	button.shutter,
	button.secondary {
		background: transparent;
		color: var(--cream);
		border: 1px solid var(--border-strong);
		padding: var(--space-4) 44px;
		font-size: var(--text-xs);
		letter-spacing: 3px;
		text-transform: uppercase;
		cursor: pointer;
		transition: all var(--dur);
		font-family: var(--font-body);
		border-radius: var(--radius-0);
	}

	button.shutter {
		border-color: var(--sage);
		color: var(--sage);
		background: var(--sage-veil);
	}

	button.shutter:hover:not(:disabled) {
		background: var(--sage);
		color: var(--bg);
	}

	button.secondary {
		padding: var(--space-4) var(--space-7);
		color: var(--text-muted);
	}

	button.secondary:hover:not(:disabled) {
		border-color: var(--rose);
		color: var(--rose);
	}

	button:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	.warning-message {
		color: var(--gold);
		font-size: var(--text-xs);
		text-align: center;
		margin: 0;
		min-height: 20px;
	}

	.review-hint {
		color: var(--text-muted);
		font-size: var(--text-xs);
		text-align: center;
		margin: 0;
	}

	.analysis-failed {
		width: 100%;
		padding: var(--space-6) var(--space-7);
		border: 1px solid var(--gold-line);
		background: var(--gold-veil);
	}

	.analysis-failed h2 {
		font-family: var(--font-display);
		font-size: var(--text-lg);
		font-weight: var(--weight-regular);
		color: var(--cream);
		margin: 0 0 var(--space-2);
	}

	.analysis-failed p {
		font-size: var(--text-sm);
		line-height: 1.7;
		color: var(--text-muted);
		margin: 0 0 var(--space-3);
	}

	.analysis-failed ul {
		margin: 0;
		padding-left: var(--space-4);
		list-style: disc;
	}

	.analysis-failed li {
		font-size: var(--text-xs);
		line-height: 1.8;
		color: var(--text-muted);
	}

	.secondary-link {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		padding: var(--space-4) var(--space-7);
		border: 1px solid var(--border);
		color: var(--text-muted);
		font-size: var(--text-xs);
		letter-spacing: 3px;
		text-transform: uppercase;
		text-decoration: none;
		transition: all var(--dur);
	}

	.secondary-link:hover {
		border-color: var(--rose);
		color: var(--rose);
	}

	/* --- analysis progress ----------------------------------------------- */

	.analysis {
		width: 100%;
		text-align: center;
	}

	.analysis-stage {
		font-family: var(--font-display);
		font-size: var(--text-lg);
		color: var(--cream);
		margin: 0 0 var(--space-1);
	}

	.analysis-sub {
		font-size: var(--text-xs);
		color: var(--text-muted);
		margin: 0 0 var(--space-4);
	}

	.analysis-track {
		width: 100%;
		max-width: 420px;
		height: 2px;
		margin: 0 auto var(--space-3);
		background: var(--border);
		overflow: hidden;
	}

	.analysis-fill {
		height: 100%;
		background: var(--sage);
		transition: width var(--dur-slow) var(--ease);
	}

	.analysis-steps {
		font-size: var(--text-2xs);
		letter-spacing: 2px;
		text-transform: uppercase;
		color: var(--text-muted);
	}

	/* --- privacy ---------------------------------------------------------- */

	.privacy-note {
		margin: var(--space-7) auto 0;
		padding: var(--space-4) var(--space-5);
		border: 1px solid var(--border);
		background: var(--sage-veil);
		text-align: left;
	}

	.privacy-note h2 {
		font-family: var(--font-body);
		font-size: var(--text-2xs);
		letter-spacing: 2px;
		text-transform: uppercase;
		color: var(--sage);
		margin: 0 0 var(--space-2);
		font-weight: var(--weight-medium);
	}

	.privacy-note ul {
		margin: 0;
		padding-left: var(--space-4);
		list-style: disc;
	}

	.privacy-note li {
		font-size: var(--text-xs);
		line-height: 1.7;
		color: var(--text-muted);
	}

	.privacy-note a {
		color: var(--sage);
		text-decoration: underline;
	}

	@media (max-width: 768px) {
		.capture-page {
			padding: var(--space-24) var(--space-5) var(--space-12);
		}

		h1 {
			font-size: var(--text-xl);
		}

		/* Portrait framing: a face is taller than it is wide. */
		.viewfinder {
			aspect-ratio: 3 / 4;
		}

		.control-row {
			flex-direction: column;
		}

		button.shutter,
		button.secondary {
			width: 100%;
		}
	}

	.camera-error p {
		margin: 0;
	}

	.camera-error-alt {
		margin-top: var(--space-2);
		color: var(--text-muted);
	}
</style>

<div class="capture-page" use:loader={loading}>
	<div class="page-head">
		<h1>Let&rsquo;s get a close-up of your face</h1>
		<a class="back-link" href="/">Go Back</a>
	</div>

	<ul class="tips">
		<li>Find a well-lit spot, facing the light</li>
		<li>Remove glasses and pull hair back</li>
		<li>Look straight ahead, neutral expression</li>
	</ul>

	<div class="viewfinder">
		<!-- svelte-ignore a11y-media-has-caption -->
		<video
			bind:this={videoElement}
			autoplay
			playsinline
			muted
			style={phase === 'review' ? 'display:none' : ''}
		></video>

		{#if phase === 'review' && capturedPreview}
			<img class="captured" src={capturedPreview} alt="Your selfie, ready to review" />
		{/if}

		{#if phase === 'live'}
			<div class="face-guide"></div>
			<span class="guide-hint">Fit your face inside the oval</span>
		{/if}

		{#if phase === 'starting' && !cameraError}
			<div class="starting">Starting your camera&hellip;</div>
		{/if}

		{#if cameraError}
			<div class="camera-error">
				<p>{cameraError}</p>
				<p class="camera-error-alt">
					{native
						? 'Or choose a photo you already have.'
						: 'You can upload a photo instead.'}
				</p>
			</div>
		{/if}
	</div>

	<div class="controls">
		{#if analysisFailed}
			<div class="analysis-failed" role="alert">
				<h2>We couldn&rsquo;t read your skin from that photo</h2>
				<p>
					This usually means the lighting was uneven, the camera was too far away, or
					part of the face was covered. Nothing was saved, so try one more time.
				</p>
				<ul>
					<li>Face a window or lamp, not away from it</li>
					<li>Fill the oval with your face</li>
					<li>Take off glasses and pull hair back</li>
				</ul>
			</div>
			<div class="control-row">
				<button class="shutter" type="button" on:click={tryAgainAfterFailure}>Try Again</button>
				<a class="secondary-link" href="/">Back to home</a>
			</div>
		{:else if phase === 'analyzing'}
			<div class="analysis" aria-live="polite">
				<p class="analysis-stage">{analysisStage}</p>
				<p class="analysis-sub">This usually takes a minute or two. You can leave this tab open.</p>
				<div class="analysis-track">
					<div
						class="analysis-fill"
						style="width: {Math.round(((stageIndex + 1) / ANALYSIS_STAGES.length) * 100)}%"
					></div>
				</div>
				<div class="analysis-steps">Step {stageIndex + 1} of {ANALYSIS_STAGES.length}</div>
			</div>
		{:else if phase === 'review'}
			<p class="review-hint">Happy with this one? Nothing is uploaded until you choose.</p>
			<div class="control-row">
				<button class="shutter" type="button" on:click={submitPhoto} disabled={!capturedBlob}>
					{capturedBlob ? 'Use This Photo' : 'Preparing…'}
				</button>
				<button class="secondary" type="button" on:click={retake}>
					{source === 'upload' ? 'Choose Another' : 'Retake'}
				</button>
			</div>
		{:else}
			{#if captureError}
				<p class="capture-error" role="alert">{captureError}</p>
			{/if}
			{#if uploadError}
				<p class="capture-error" role="alert">{uploadError}</p>
			{/if}
			<p class="warning-message">{warningMessage}</p>
			<div class="control-row">
				{#if cameraError && native}
					<!-- In the app the phone's own camera is still there even when
					     the webview's is not, so that is the way forward rather
					     than an instruction to go and change a setting. -->
					<button class="shutter" type="button" on:click={takeNativePhoto}>
						Take a Photo
					</button>
					<button class="secondary" type="button" on:click={chooseFile}>
						Choose a Photo
					</button>
				{:else if cameraError}
					<!-- No camera to shoot with, so the upload is the way forward and
					     takes the screen's single primary action. -->
					<button class="shutter" type="button" on:click={chooseFile}>
						Upload a Photo
					</button>
					<button class="secondary" type="button" on:click={setupWebcam}>
						Try the Camera Again
					</button>
				{:else}
					<button
						class="shutter"
						type="button"
						on:click={capturePhoto}
						disabled={phase !== 'live'}
					>
						Snap Photo
					</button>
					<button class="secondary" type="button" on:click={chooseFile}>
						{native ? 'Choose a Photo' : 'Upload a Photo'}
					</button>
				{/if}
			</div>
		{/if}
	</div>

	{#if !native}
		<input
			class="u-visually-hidden"
			type="file"
			accept="image/*"
			bind:this={fileInput}
			on:change={onFileChosen}
			aria-label="Upload a photo of your face"
		/>
	{/if}

	<div class="privacy-note">
		<h2>What happens to your photo</h2>
		<ul>
			<li>Your photo is sent to SkinGenie once, analysed, and then it is gone. We do not store it.</li>
			<li>It is never shared with brands, retailers, or advertisers.</li>
			<li>Whether you take it or upload it, nothing leaves your device until you choose Use This Photo.</li>
			<li>The marked-up copy you see next stays on this device, and <a href="/delete-account">deleting your account</a> clears it. <a href="/privacy#photos">How we handle photos</a>.</li>
		</ul>
	</div>
</div>
